import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Image, Tag, Flame, Award, Palette, Save, Plus, X, ExternalLink, Package, Search } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const StoreSettings = () => {
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form states
  const [description, setDescription] = useState('');
  const [themeColor, setThemeColor] = useState('#f97316');
  const [storeCategories, setStoreCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [featuredIds, setFeaturedIds] = useState([]);
  const [deals, setDeals] = useState([]);
  const [campaignTitle, setCampaignTitle] = useState('');
  const [campaignDesc, setCampaignDesc] = useState('');
  const [campaignDiscount, setCampaignDiscount] = useState(0);
  const [campaignEnd, setCampaignEnd] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/seller/store-settings`, { headers: { Authorization: `Bearer ${token}` } });
      const s = res.data.store;
      setStore(s);
      setProducts(res.data.products);
      setDescription(s.store_description || '');
      setThemeColor(s.theme_color || '#f97316');
      setStoreCategories(s.store_categories || []);
      setFeaturedIds(s.featured_product_ids || []);
      setDeals(s.deal_products || []);
    } catch (err) {
      toast.error('Ayarlar yuklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/seller/store-settings`, {
        store_description: description,
        theme_color: themeColor,
        store_categories: storeCategories,
        featured_product_ids: featuredIds,
      }, { headers: { Authorization: `Bearer ${token}` } });

      await axios.put(`${API}/seller/deal-products`, { deals }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Magaza ayarlari kaydedildi!');
    } catch (err) {
      toast.error('Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/seller/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const token2 = localStorage.getItem('token');
      await axios.put(`${API}/seller/store-settings`, { [type]: res.data.url }, { headers: { Authorization: `Bearer ${token2}` } });
      toast.success(`${type === 'banner_url' ? 'Banner' : 'Logo'} yuklendi!`);
      fetchSettings();
    } catch (err) {
      toast.error('Yuklenemedi');
    }
  };

  const createCampaign = async () => {
    if (!campaignTitle) { toast.error('Kampanya baslik giriniz'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/seller/campaigns`, {
        title: campaignTitle,
        description: campaignDesc,
        discount_percent: campaignDiscount,
        end_date: campaignEnd || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Kampanya olusturuldu!');
      setCampaignTitle(''); setCampaignDesc(''); setCampaignDiscount(0); setCampaignEnd('');
      fetchSettings();
    } catch (err) {
      toast.error('Kampanya olusturulamadi');
    }
  };

  const deleteCampaign = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/seller/campaigns/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Kampanya silindi');
      fetchSettings();
    } catch (err) { toast.error('Silinemedi'); }
  };

  const toggleFeatured = (productId) => {
    setFeaturedIds(prev => prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]);
  };

  const addDeal = (product) => {
    if (deals.find(d => d.product_id === product.id)) return;
    setDeals([...deals, { product_id: product.id, deal_price: Math.round((product.display_price || product.price || 0) * 0.8), original_price: product.display_price || product.price || 0, discount_percent: 20 }]);
  };

  const removeDeal = (productId) => {
    setDeals(deals.filter(d => d.product_id !== productId));
  };

  const updateDeal = (productId, field, value) => {
    setDeals(deals.map(d => d.product_id === productId ? { ...d, [field]: value } : d));
  };

  const filteredProducts = products.filter(p => !searchTerm || p.name.toLowerCase().includes(searchTerm.toLowerCase()) || (p.oem_code || '').toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full" /></div>;

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Magaza Ayarlari</h1>
        <div className="flex gap-3">
          {store?.slug && (
            <a href={`/store/${store.slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-4 py-2 border rounded-xl text-sm text-slate-600 hover:bg-slate-50">
              <ExternalLink className="h-4 w-4" /> Magazami Gor
            </a>
          )}
          <button onClick={saveSettings} disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50" data-testid="save-settings">
            <Save className="h-4 w-4" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>

      {/* Banner Upload */}
      <div className="bg-white rounded-xl border p-5">
        <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><Image className="h-5 w-5 text-orange-500" /> Banner Gorseli</h2>
        {store?.banner_url && (
          <div className="relative h-40 rounded-xl overflow-hidden mb-3 bg-slate-100">
            <img src={store.banner_url.startsWith('http') ? store.banner_url : `${BACKEND_URL}${store.banner_url}`} alt="Banner" className="w-full h-full object-cover" />
          </div>
        )}
        <label className="flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-xl cursor-pointer hover:border-orange-400 transition-colors text-sm text-slate-500">
          <Upload className="h-4 w-4" /> Banner Yukle (Onerilen: 1200x300px)
          <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && uploadImage(e.target.files[0], 'banner_url')} />
        </label>
      </div>

      {/* Logo Upload */}
      <div className="bg-white rounded-xl border p-5">
        <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><Image className="h-5 w-5 text-orange-500" /> Logo</h2>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-xl border bg-slate-100 flex items-center justify-center overflow-hidden">
            {store?.logo_url ? (
              <img src={store.logo_url.startsWith('http') ? store.logo_url : `${BACKEND_URL}${store.logo_url}`} alt="Logo" className="w-full h-full object-cover" />
            ) : (
              <span className="text-2xl font-bold text-slate-400">{store?.name?.charAt(0)}</span>
            )}
          </div>
          <label className="flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-xl cursor-pointer hover:border-orange-400 text-sm text-slate-500">
            <Upload className="h-4 w-4" /> Logo Yukle
            <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && uploadImage(e.target.files[0], 'logo_url')} />
          </label>
        </div>
      </div>

      {/* Description + Theme */}
      <div className="bg-white rounded-xl border p-5 space-y-4">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Palette className="h-5 w-5 text-orange-500" /> Genel Ayarlar</h2>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Magaza Aciklamasi</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} className="w-full border rounded-xl p-3 text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-orange-200" placeholder="Magazanizi tanitin..." data-testid="store-description" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Tema Rengi</label>
          <div className="flex items-center gap-3">
            <input type="color" value={themeColor} onChange={e => setThemeColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer border-0" />
            <input value={themeColor} onChange={e => setThemeColor(e.target.value)} className="border rounded-lg px-3 py-2 text-sm w-28" />
            <div className="flex gap-2">
              {['#f97316', '#ef4444', '#3b82f6', '#10b981', '#8b5cf6', '#000000'].map(c => (
                <button key={c} onClick={() => setThemeColor(c)} className={`w-8 h-8 rounded-full border-2 ${themeColor === c ? 'border-slate-900 scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="bg-white rounded-xl border p-5">
        <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><Tag className="h-5 w-5 text-orange-500" /> Magaza Kategorileri</h2>
        <div className="flex flex-wrap gap-2 mb-3">
          {storeCategories.map((cat, idx) => (
            <span key={idx} className="flex items-center gap-1 px-3 py-1.5 bg-orange-50 text-orange-700 rounded-full text-sm">
              {cat}
              <button onClick={() => setStoreCategories(storeCategories.filter((_, i) => i !== idx))} className="hover:text-red-500"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={newCategory} onChange={e => setNewCategory(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && newCategory.trim()) { setStoreCategories([...storeCategories, newCategory.trim()]); setNewCategory(''); }}} placeholder="Yeni kategori ekle..." className="flex-1 border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200" />
          <button onClick={() => { if (newCategory.trim()) { setStoreCategories([...storeCategories, newCategory.trim()]); setNewCategory(''); }}} className="px-4 py-2 bg-orange-500 text-white rounded-xl text-sm"><Plus className="h-4 w-4" /></button>
        </div>
      </div>

      {/* Featured Products */}
      <div className="bg-white rounded-xl border p-5">
        <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><Award className="h-5 w-5 text-orange-500" /> One Cikan Urunler ({featuredIds.length})</h2>
        <div className="mb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Urun ara..." className="w-full pl-10 pr-4 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-200" />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto space-y-2">
          {filteredProducts.map(p => (
            <div key={p.id} onClick={() => toggleFeatured(p.id)} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${featuredIds.includes(p.id) ? 'bg-orange-50 border border-orange-200' : 'hover:bg-slate-50 border border-transparent'}`}>
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${featuredIds.includes(p.id) ? 'bg-orange-500 border-orange-500 text-white' : 'border-slate-300'}`}>
                {featuredIds.includes(p.id) && <span className="text-xs">✓</span>}
              </div>
              <div className="w-10 h-10 bg-slate-100 rounded flex-shrink-0">
                {(p.image_url || p.images?.[0]) && <img src={(p.image_url || p.images?.[0]).startsWith('http') ? (p.image_url || p.images?.[0]) : `${BACKEND_URL}${p.image_url || p.images?.[0]}`} alt="" className="w-full h-full object-contain" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{p.name}</p>
                <p className="text-xs text-slate-500">{p.brand} | {p.oem_code}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Deal Products */}
      <div className="bg-white rounded-xl border p-5">
        <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><Flame className="h-5 w-5 text-red-500" /> Firsat Urunleri ({deals.length})</h2>
        {deals.map(d => {
          const prod = products.find(p => p.id === d.product_id);
          return (
            <div key={d.product_id} className="flex items-center gap-3 p-3 border rounded-xl mb-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{prod?.name || d.product_id}</p>
              </div>
              <div className="flex items-center gap-2">
                <div>
                  <label className="text-xs text-slate-500">Eski Fiyat</label>
                  <input type="number" value={d.original_price} onChange={e => updateDeal(d.product_id, 'original_price', Number(e.target.value))} className="w-24 border rounded-lg px-2 py-1 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Firsat Fiyat</label>
                  <input type="number" value={d.deal_price} onChange={e => updateDeal(d.product_id, 'deal_price', Number(e.target.value))} className="w-24 border rounded-lg px-2 py-1 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">%</label>
                  <input type="number" value={d.discount_percent} onChange={e => updateDeal(d.product_id, 'discount_percent', Number(e.target.value))} className="w-16 border rounded-lg px-2 py-1 text-sm" />
                </div>
                <button onClick={() => removeDeal(d.product_id)} className="text-red-500 hover:text-red-700 mt-4"><X className="h-4 w-4" /></button>
              </div>
            </div>
          );
        })}
        <p className="text-xs text-slate-500 mt-2 mb-2">Firsat olarak eklemek istediginiz urune tiklayin:</p>
        <div className="max-h-48 overflow-y-auto space-y-1">
          {filteredProducts.filter(p => !deals.find(d => d.product_id === p.id)).map(p => (
            <div key={p.id} onClick={() => addDeal(p)} className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-red-50 text-sm">
              <Plus className="h-4 w-4 text-red-500" />
              <span className="truncate">{p.name}</span>
              <span className="text-xs text-slate-400 ml-auto">{p.display_price || p.price} TL</span>
            </div>
          ))}
        </div>
      </div>

      {/* Campaigns */}
      <div className="bg-white rounded-xl border p-5">
        <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><Tag className="h-5 w-5 text-orange-500" /> Kampanyalar</h2>
        {(store?.campaigns || []).map(c => (
          <div key={c.id} className="flex items-center justify-between p-3 border rounded-xl mb-2 bg-orange-50">
            <div>
              <p className="font-medium text-sm">{c.title}</p>
              <p className="text-xs text-slate-500">{c.description} {c.discount_percent > 0 && `| %${c.discount_percent} indirim`}</p>
            </div>
            <button onClick={() => deleteCampaign(c.id)} className="text-red-500 hover:text-red-700"><X className="h-4 w-4" /></button>
          </div>
        ))}
        <div className="border-t pt-3 mt-3 space-y-2">
          <input value={campaignTitle} onChange={e => setCampaignTitle(e.target.value)} placeholder="Kampanya basligi" className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200" />
          <input value={campaignDesc} onChange={e => setCampaignDesc(e.target.value)} placeholder="Aciklama" className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200" />
          <div className="flex gap-2">
            <input type="number" value={campaignDiscount} onChange={e => setCampaignDiscount(Number(e.target.value))} placeholder="%" className="w-20 border rounded-xl px-3 py-2 text-sm" />
            <input type="date" value={campaignEnd} onChange={e => setCampaignEnd(e.target.value)} className="flex-1 border rounded-xl px-3 py-2 text-sm" />
            <button onClick={createCampaign} className="px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600">Olustur</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreSettings;
